/**
 * WebhookEndpointsPanel — M12d-4b: Verwaltung von Webhook-Endpoints.
 *
 * Wird ausschließlich gerendert, wenn `REACT_APP_API_V2_ENABLED === "true"`
 * (Gate in ApiAccessSection.tsx, nicht hier). Selbst dann bleibt das Backend
 * fail-closed hinter `WEBHOOKS_ENABLED` (server env) — `listWebhookEndpoints`
 * wirft dann `failed-precondition`, was hier als freundlicher Hinweis
 * ("noch nicht aktiviert") statt als roher Fehler dargestellt wird.
 *
 * Muster/Styling: ApiAccessSection.tsx (Klartext-Reveal-Block, ConfirmationDialog
 * für destruktive Aktionen, Double-Click-Guards via lokale Loading-Flags).
 */

import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  IconGlobe,
  IconCopy,
  IconCheck,
  IconDelete,
  IconRefresh,
  IconNew,
  IconEdit,
  IconSave,
  IconClose,
  IconToggleOn,
  IconToggleOff,
} from "../../icons";
import { ConfirmationDialog } from "../ConfirmationDialog";
import {
  createWebhookEndpoint,
  listWebhookEndpoints,
  updateWebhookEndpoint,
  deleteWebhookEndpoint,
  rotateWebhookSecret,
  WebhookEndpointItem,
  WEBHOOK_EVENT_TYPES,
  WebhookEventType,
} from "../../api/webhooksApi";
import logger from "../../utils/logger";
import { formatDate as formatDateCentral } from "../../utils/formatters";

/** i18n-Key-Suffix je Event-Typ (z.B. "invoice.created" -> "created"). */
const EVENT_LABEL_KEYS: Record<WebhookEventType, string> = {
  "invoice.created": "created",
  "invoice.paid": "paid",
  "invoice.cancelled": "cancelled",
};

/** Extrahiert den Function-Error-Code aus einem von webhooksApi geworfenen Error, falls vorhanden. */
function getErrorCode(error: unknown): string | undefined {
  const anyErr = error as { code?: string };
  return typeof anyErr?.code === "string" ? anyErr.code : undefined;
}

/** Shape gemeinsam für create- und rotate-Reveal (beide liefern identische Felder). */
interface RevealedSecret {
  endpointId: string;
  signingSecret: string;
  url: string;
}

const WebhookEndpointsPanel: React.FC = () => {
  const { t, i18n } = useTranslation("account");

  // --- Liste ---
  const [endpoints, setEndpoints] = useState<WebhookEndpointItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [notEnabled, setNotEnabled] = useState<boolean>(false);

  // --- Erstellen ---
  const [urlInput, setUrlInput] = useState<string>("");
  const [selectedEvents, setSelectedEvents] = useState<string[]>([]);
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // --- Einmaliger Secret-Reveal (create ODER rotate) ---
  const [revealedSecret, setRevealedSecret] = useState<RevealedSecret | null>(null);
  const [copied, setCopied] = useState<boolean>(false);

  // --- Bearbeiten (inline) ---
  const [editTarget, setEditTarget] = useState<WebhookEndpointItem | null>(null);
  const [editUrlInput, setEditUrlInput] = useState<string>("");
  const [editEvents, setEditEvents] = useState<string[]>([]);
  const [isSavingEdit, setIsSavingEdit] = useState<boolean>(false);
  const [editError, setEditError] = useState<string | null>(null);

  // --- Active-Toggle ---
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [toggleError, setToggleError] = useState<string | null>(null);

  // --- Rotieren ---
  const [rotateTarget, setRotateTarget] = useState<WebhookEndpointItem | null>(null);
  const [isRotating, setIsRotating] = useState<boolean>(false);
  const [rotateError, setRotateError] = useState<string | null>(null);

  // --- Löschen ---
  const [deleteTarget, setDeleteTarget] = useState<WebhookEndpointItem | null>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // --- Laden der Endpoint-Liste ---
  const loadEndpoints = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const fetched = await listWebhookEndpoints();
      setEndpoints(fetched);
      setNotEnabled(false);
    } catch (error: unknown) {
      logger.error("WebhookEndpointsPanel: listWebhookEndpoints failed", error);
      if (getErrorCode(error) === "failed-precondition") {
        // Feature ist serverseitig noch nicht aktiv — freundlicher Hinweis statt Fehler.
        setNotEnabled(true);
        setEndpoints([]);
      } else {
        setLoadError(t("webhooks.error"));
      }
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadEndpoints();
  }, [loadEndpoints]);

  // --- Event-Checkbox togglen (gemeinsam für Erstellen/Bearbeiten-Formular) ---
  const toggleEventIn = (
    setter: React.Dispatch<React.SetStateAction<string[]>>,
    event: string
  ) => {
    setter((prev) =>
      prev.includes(event) ? prev.filter((e) => e !== event) : [...prev, event]
    );
  };

  // --- Endpoint erstellen ---
  const handleCreate = async () => {
    if (isCreating) return;
    if (!urlInput.trim() || selectedEvents.length === 0) {
      setCreateError(t("webhooks.noEventsSelected"));
      return;
    }
    setIsCreating(true);
    setCreateError(null);
    setRevealedSecret(null);
    setCopied(false);
    try {
      const result = await createWebhookEndpoint(urlInput.trim(), selectedEvents);
      setRevealedSecret({
        endpointId: result.endpointId,
        signingSecret: result.signingSecret,
        url: result.url,
      });
      setUrlInput("");
      setSelectedEvents([]);
      await loadEndpoints();
    } catch (error: unknown) {
      logger.error("WebhookEndpointsPanel: createWebhookEndpoint failed", error);
      setCreateError(t("webhooks.error"));
    } finally {
      setIsCreating(false);
    }
  };

  // --- Secret in Clipboard kopieren ---
  const handleCopy = async () => {
    if (!revealedSecret?.signingSecret) return;
    try {
      await navigator.clipboard.writeText(revealedSecret.signingSecret);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (error: unknown) {
      logger.error("WebhookEndpointsPanel: clipboard write failed", error);
    }
  };

  // --- Active-Status togglen ---
  const handleToggleActive = async (item: WebhookEndpointItem) => {
    if (togglingId) return;
    setTogglingId(item.id);
    setToggleError(null);
    try {
      await updateWebhookEndpoint({ endpointId: item.id, active: !item.active });
      await loadEndpoints();
    } catch (error: unknown) {
      logger.error(`WebhookEndpointsPanel: updateWebhookEndpoint (toggle) failed for ${item.id}`, error);
      setToggleError(t("webhooks.error"));
    } finally {
      setTogglingId(null);
    }
  };

  // --- Bearbeiten starten ---
  const handleEditStart = (item: WebhookEndpointItem) => {
    setEditTarget(item);
    setEditUrlInput(item.url);
    setEditEvents(item.events);
    setEditError(null);
  };

  // --- Bearbeiten abbrechen ---
  const handleEditCancel = () => {
    if (isSavingEdit) return;
    setEditTarget(null);
    setEditError(null);
  };

  // --- Bearbeiten speichern ---
  const handleEditSave = async () => {
    if (!editTarget || isSavingEdit) return;
    if (!editUrlInput.trim() || editEvents.length === 0) {
      setEditError(t("webhooks.noEventsSelected"));
      return;
    }
    setIsSavingEdit(true);
    setEditError(null);
    try {
      await updateWebhookEndpoint({
        endpointId: editTarget.id,
        url: editUrlInput.trim(),
        events: editEvents,
      });
      setEditTarget(null);
      await loadEndpoints();
    } catch (error: unknown) {
      logger.error(`WebhookEndpointsPanel: updateWebhookEndpoint (edit) failed for ${editTarget.id}`, error);
      setEditError(t("webhooks.error"));
    } finally {
      setIsSavingEdit(false);
    }
  };

  // --- Secret rotieren (nach Bestätigung) ---
  const handleRotateConfirmed = async () => {
    if (!rotateTarget || isRotating) return;
    setIsRotating(true);
    setRotateError(null);
    try {
      const result = await rotateWebhookSecret(rotateTarget.id);
      setRotateTarget(null);
      setRevealedSecret({
        endpointId: result.endpointId,
        signingSecret: result.signingSecret,
        url: result.url,
      });
      setCopied(false);
      await loadEndpoints();
    } catch (error: unknown) {
      logger.error(`WebhookEndpointsPanel: rotateWebhookSecret failed for ${rotateTarget.id}`, error);
      setRotateError(t("webhooks.error"));
      setRotateTarget(null);
    } finally {
      setIsRotating(false);
    }
  };

  // --- Löschen (nach Bestätigung) ---
  const handleDeleteConfirmed = async () => {
    if (!deleteTarget || isDeleting) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await deleteWebhookEndpoint(deleteTarget.id);
      setDeleteTarget(null);
      await loadEndpoints();
    } catch (error: unknown) {
      logger.error(`WebhookEndpointsPanel: deleteWebhookEndpoint failed for ${deleteTarget.id}`, error);
      setDeleteError(t("webhooks.error"));
      setDeleteTarget(null);
    } finally {
      setIsDeleting(false);
    }
  };

  // --- Datum formatieren (ISO → lokale Darstellung, zentrales formatDate, M-G) ---
  const formatDate = (iso: string | null): string => {
    if (!iso) return t("webhooks.neverDelivered");
    return formatDateCentral(iso, { locale: i18n.language, includeTime: true });
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className="mt-8 bg-surface-raised shadow-lg rounded-xl border border-hairline p-6">
      {/* Abschnitts-Header */}
      <h2 className="text-xl font-semibold mb-2 text-primary flex items-center">
        <IconGlobe size={20} className="mr-2 text-accent-ink" />
        {t("webhooks.title")}
      </h2>
      <p className="text-muted text-sm mb-6">{t("webhooks.description")}</p>

      {isLoading ? (
        <div className="flex items-center gap-2 text-muted text-sm py-4">
          <IconRefresh className="animate-spin" />
          <span>{t("webhooks.loading")}</span>
        </div>
      ) : notEnabled ? (
        <p className="text-muted text-sm py-2">{t("webhooks.notEnabled")}</p>
      ) : (
        <>
          {/* Neuen Endpoint erstellen */}
          <div className="mb-6 p-4 bg-[rgb(var(--surface-inset-rgb)/0.4)] rounded-lg border border-strong">
            <p className="text-secondary text-sm font-medium mb-1">{t("webhooks.urlLabel")}</p>
            <input
              type="text"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              placeholder={t("webhooks.urlPlaceholder")}
              disabled={isCreating}
              className="w-full px-3 py-2 bg-[rgb(var(--surface-base-rgb)/0.6)] border border-strong rounded-md text-primary text-sm placeholder:text-faint focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
            />

            <div className="mt-4">
              <p className="text-secondary text-xs font-medium mb-2">{t("webhooks.eventsLabel")}</p>
              <div className="flex flex-wrap gap-x-5 gap-y-2">
                {WEBHOOK_EVENT_TYPES.map((event) => (
                  <label
                    key={event}
                    className="flex items-center gap-2 text-sm text-secondary cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedEvents.includes(event)}
                      onChange={() => toggleEventIn(setSelectedEvents, event)}
                      disabled={isCreating}
                      className="rounded border-strong bg-[rgb(var(--surface-base-rgb)/0.6)] text-indigo-500 focus:ring-indigo-500 disabled:opacity-50"
                    />
                    {t(`webhooks.event.${EVENT_LABEL_KEYS[event]}`)}
                  </label>
                ))}
              </div>
            </div>

            <button
              onClick={handleCreate}
              disabled={isCreating}
              className="mt-4 flex items-center justify-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-[#0C1322] text-sm font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isCreating ? (
                <>
                  <IconRefresh className="animate-spin" />
                  {t("webhooks.creating")}
                </>
              ) : (
                <>
                  <IconNew />
                  {t("webhooks.create")}
                </>
              )}
            </button>

            {createError && <p className="mt-3 text-sm text-[var(--tone-danger-fg)]">{createError}</p>}
          </div>

          {/* Einmaliger Signatur-Secret-Reveal (create ODER rotate) */}
          {revealedSecret && (
            <div className="mb-6 p-4 bg-[var(--tone-warning-bg)] border border-[var(--tone-warning-border)] rounded-lg">
              <p className="text-[var(--tone-warning-fg)] text-sm font-semibold mb-1">
                {t("webhooks.secretWarningOnce")}
              </p>
              <p className="text-muted text-xs mb-2 break-all">{revealedSecret.url}</p>
              <div className="flex items-center gap-3 mt-1">
                <code className="flex-1 font-mono text-xs bg-[rgb(var(--surface-base-rgb)/0.7)] text-[var(--tone-success-fg)] px-3 py-2 rounded break-all select-all">
                  {revealedSecret.signingSecret}
                </code>
                <button
                  onClick={handleCopy}
                  title={copied ? t("webhooks.copied") : t("webhooks.copy")}
                  className={`flex items-center gap-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    copied ? "bg-green-700 text-white" : "bg-surface-hover hover:bg-gray-500 text-primary"
                  }`}
                >
                  {copied ? (
                    <>
                      <IconCheck />
                      {t("webhooks.copied")}
                    </>
                  ) : (
                    <>
                      <IconCopy />
                      {t("webhooks.copy")}
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Endpoint-Liste */}
          <div>
            {loadError ? (
              <p className="text-[var(--tone-danger-fg)] text-sm py-4">{loadError}</p>
            ) : endpoints.length === 0 ? (
              <p className="text-faint text-sm py-4 text-center">{t("webhooks.noEndpoints")}</p>
            ) : (
              <div className="space-y-3">
                {endpoints.map((item) => (
                  <div
                    key={item.id}
                    className="p-4 rounded-lg border border-strong bg-[rgb(var(--surface-inset-rgb)/0.3)] transition-colors"
                  >
                    {editTarget?.id === item.id ? (
                      // --- Inline-Bearbeiten-Formular ---
                      <div>
                        <input
                          type="text"
                          value={editUrlInput}
                          onChange={(e) => setEditUrlInput(e.target.value)}
                          disabled={isSavingEdit}
                          className="w-full px-3 py-2 bg-[rgb(var(--surface-base-rgb)/0.6)] border border-strong rounded-md text-primary text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
                        />
                        <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2">
                          {WEBHOOK_EVENT_TYPES.map((event) => (
                            <label
                              key={event}
                              className="flex items-center gap-2 text-sm text-secondary cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={editEvents.includes(event)}
                                onChange={() => toggleEventIn(setEditEvents, event)}
                                disabled={isSavingEdit}
                                className="rounded border-strong bg-[rgb(var(--surface-base-rgb)/0.6)] text-indigo-500 focus:ring-indigo-500 disabled:opacity-50"
                              />
                              {t(`webhooks.event.${EVENT_LABEL_KEYS[event]}`)}
                            </label>
                          ))}
                        </div>
                        {editError && <p className="mt-2 text-sm text-[var(--tone-danger-fg)]">{editError}</p>}
                        <div className="mt-3 flex gap-2">
                          <button
                            onClick={handleEditSave}
                            disabled={isSavingEdit}
                            className="flex items-center gap-1 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-[#0C1322] text-xs font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <IconSave />
                            {t("webhooks.save")}
                          </button>
                          <button
                            onClick={handleEditCancel}
                            disabled={isSavingEdit}
                            className="flex items-center gap-1 px-3 py-1.5 bg-surface-hover hover:bg-gray-500 text-primary text-xs font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <IconClose />
                            {t("webhooks.cancel")}
                          </button>
                        </div>
                      </div>
                    ) : (
                      // --- Statische Anzeige ---
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <code className="font-mono text-xs text-accent-ink break-all">{item.url}</code>
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            {item.events.map((event) => (
                              <span
                                key={event}
                                className="px-2 py-0.5 text-xs rounded-full bg-[var(--tone-accent-bg)] text-accent-ink border border-[var(--tone-accent-border)]"
                              >
                                {t(`webhooks.event.${EVENT_LABEL_KEYS[event] ?? event}`)}
                              </span>
                            ))}
                          </div>
                          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted">
                            <span>
                              {t("webhooks.lastDelivery")}: {formatDate(item.lastDeliveryAt)}
                            </span>
                            <span>
                              {t("webhooks.failureCount")}: {item.failureCount}
                            </span>
                          </div>
                        </div>

                        <div className="flex flex-col items-end gap-2 shrink-0">
                          <button
                            onClick={() => handleToggleActive(item)}
                            disabled={togglingId === item.id}
                            title={t("webhooks.toggle")}
                            className={`flex items-center gap-1 px-2 py-1 text-xs rounded-full border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                              item.active
                                ? "bg-[var(--tone-success-bg)] text-[var(--tone-success-fg)] border-[var(--tone-success-border)] hover:brightness-110"
                                : "bg-[rgb(var(--surface-base-rgb)/0.4)] text-muted border-hairline hover:bg-[rgb(var(--surface-raised-rgb)/0.6)]"
                            }`}
                          >
                            {item.active ? <IconToggleOn /> : <IconToggleOff />}
                            {item.active ? t("webhooks.active") : t("webhooks.inactive")}
                          </button>
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleEditStart(item)}
                              title={t("webhooks.edit")}
                              className="flex items-center gap-1 px-3 py-1.5 bg-[var(--tone-accent-bg)] hover:bg-[var(--tone-accent-bg)] hover:brightness-110 text-accent-ink text-xs rounded-md border border-[var(--tone-accent-border)] transition-colors"
                            >
                              <IconEdit />
                              {t("webhooks.edit")}
                            </button>
                            <button
                              onClick={() => setRotateTarget(item)}
                              disabled={isRotating}
                              title={t("webhooks.rotate")}
                              className="flex items-center gap-1 px-3 py-1.5 bg-[var(--tone-accent-bg)] hover:bg-[var(--tone-accent-bg)] hover:brightness-110 text-accent-ink text-xs rounded-md border border-[var(--tone-accent-border)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <IconRefresh />
                              {t("webhooks.rotate")}
                            </button>
                            <button
                              onClick={() => setDeleteTarget(item)}
                              disabled={isDeleting}
                              title={t("webhooks.delete")}
                              className="flex items-center gap-1 px-3 py-1.5 bg-[var(--tone-danger-bg)] hover:bg-[var(--tone-danger-bg)] hover:brightness-110 text-[var(--tone-danger-fg)] text-xs rounded-md border border-[var(--tone-danger-border)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <IconDelete />
                              {t("webhooks.delete")}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {toggleError && <p className="mt-3 text-sm text-[var(--tone-danger-fg)]">{toggleError}</p>}
            {rotateError && <p className="mt-3 text-sm text-[var(--tone-danger-fg)]">{rotateError}</p>}
            {deleteError && <p className="mt-3 text-sm text-[var(--tone-danger-fg)]">{deleteError}</p>}
          </div>
        </>
      )}

      {/* Rotations-Bestätigungsdialog */}
      <ConfirmationDialog
        isOpen={rotateTarget !== null}
        title={t("webhooks.rotateConfirmTitle")}
        message={t("webhooks.rotateConfirmMessage", { url: rotateTarget?.url ?? "" })}
        options={[
          {
            label: isRotating ? t("webhooks.rotating") : t("webhooks.rotate"),
            variant: "primary",
            action: () => {
              if (!isRotating) {
                handleRotateConfirmed();
              }
            },
          },
          {
            label: t("webhooks.cancel"),
            variant: "secondary",
            action: () => setRotateTarget(null),
          },
        ]}
        onClose={() => {
          if (!isRotating) {
            setRotateTarget(null);
          }
        }}
      />

      {/* Lösch-Bestätigungsdialog */}
      <ConfirmationDialog
        isOpen={deleteTarget !== null}
        title={t("webhooks.deleteConfirmTitle")}
        message={t("webhooks.deleteConfirmMessage", { url: deleteTarget?.url ?? "" })}
        options={[
          {
            label: t("webhooks.delete"),
            variant: "danger",
            action: handleDeleteConfirmed,
          },
          {
            label: t("webhooks.cancel"),
            variant: "secondary",
            action: () => setDeleteTarget(null),
          },
        ]}
        onClose={() => setDeleteTarget(null)}
      />
    </div>
  );
};

export default WebhookEndpointsPanel;
